import React, { useEffect, useId, useRef, useState } from 'react';
import { useModal } from '../contexts/ModalContext';
import Dialog from '../ui/Dialog/Dialog';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import { ICONS } from '../ui/icon-map';
import '../ui/Dialog/dialog.css';
import '../ui/Field/field.css';

const TOAST_ICON = {
    success: ICONS.success,
    error: ICONS.error,
    warning: ICONS.warning,
    info: ICONS.info,
};

/**
 * Renders the global message dialog (confirm / alert / prompt) and the toast stack.
 * Mounted once in App.
 */
export default function GlobalModal() {
    const { modal, settle, toasts, dismissToast } = useModal();
    const { isOpen, type, props = {} } = modal;
    const [inputValue, setInputValue] = useState('');
    const [touched, setTouched] = useState(false);
    const inputRef = useRef(null);
    const cancelRef = useRef(null);
    const okRef = useRef(null);
    const uid = useId();

    useEffect(() => {
        if (isOpen && type === 'prompt') {
            setInputValue(props.initialValue || '');
            setTouched(false);
        }
    }, [isOpen, type, props, modal.id]);

    const danger = type === 'confirm' ? props.isDangerous : type === 'alert' ? props.isError : false;
    const tone = danger ? 'danger' : (type === 'alert' && props.tone === 'success' ? 'success' : 'accent');
    const icon = type === 'prompt' ? ICONS.edit : danger ? ICONS.alert : (type === 'confirm' ? ICONS.info : ICONS.info);
    const promptInvalid = type === 'prompt' && props.required && touched && !String(inputValue).trim();

    const cancel = () => settle(type === 'prompt' ? null : false);
    const confirm = () => {
        if (type === 'prompt') {
            if (props.required && !String(inputValue).trim()) {
                setTouched(true);
                inputRef.current && inputRef.current.focus();
                return;
            }
            settle(inputValue);
        } else if (type === 'alert') {
            settle(undefined);
        } else {
            settle(true);
        }
    };

    const initialFocus = type === 'prompt' ? inputRef : (type === 'confirm' && danger ? cancelRef : okRef);

    const footer = isOpen ? (
        <>
            {type !== 'alert' && (
                <Button ref={cancelRef} variant="secondary" onClick={cancel}>
                    {props.cancelLabel || 'Cancel'}
                </Button>
            )}
            <Button
                ref={okRef}
                variant={danger && type === 'confirm' ? 'danger' : 'primary'}
                onClick={confirm}
            >
                {type === 'alert' ? (props.buttonLabel || 'OK') : (props.confirmLabel || (type === 'prompt' ? 'Submit' : 'Confirm'))}
            </Button>
        </>
    ) : null;

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={cancel}
                title={props.title}
                size="sm"
                role={type === 'prompt' ? 'dialog' : 'alertdialog'}
                closeOnOverlay={type !== 'alert'}
                initialFocusRef={initialFocus}
                footer={footer}
            >
                <div className="ds-message">
                    <span className={`ds-message__icon ds-message__icon--${tone}`}>
                        <Icon glyph={icon} size="sm" />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {props.message ? <div className="ds-message__text">{props.message}</div> : null}
                        {type === 'prompt' && (
                            <div className="ds-message__input ds-field">
                                {props.label && <label className="ds-field__label" htmlFor={`${uid}-prompt`}>{props.label}</label>}
                                <input
                                    id={`${uid}-prompt`}
                                    ref={inputRef}
                                    type={props.inputType || 'text'}
                                    dir="auto"
                                    className={`ds-input ${promptInvalid ? 'is-invalid' : ''}`}
                                    placeholder={props.placeholder}
                                    value={inputValue}
                                    aria-label={props.label ? undefined : (props.title || 'Value')}
                                    aria-invalid={promptInvalid || undefined}
                                    aria-describedby={promptInvalid ? `${uid}-prompt-err` : undefined}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onBlur={() => setTouched(true)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            confirm();
                                        }
                                    }}
                                />
                                {promptInvalid && <div id={`${uid}-prompt-err`} className="ds-field__error">This field is required.</div>}
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>

            <div className="toast-viewport" role="region" aria-label="Notifications">
                {toasts.map((t) => (
                    <div
                        key={t.id}
                        className={`ds-toast ds-toast--${t.type}`}
                        role={t.type === 'error' ? 'alert' : 'status'}
                        aria-live={t.type === 'error' ? 'assertive' : 'polite'}
                    >
                        <span className="ds-toast__icon"><Icon glyph={TOAST_ICON[t.type] || ICONS.info} size="sm" /></span>
                        <div className="ds-toast__message" dir="auto">{t.message}</div>
                        <button type="button" className="ds-toast__close" onClick={() => dismissToast(t.id)} aria-label="Dismiss notification">
                            <Icon glyph={ICONS.close} size="xs" />
                        </button>
                    </div>
                ))}
            </div>
        </>
    );
}
